import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const ClientBenefits = () => {
  const [searchParams] = useSearchParams();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const practiceArea = searchParams.get('area') || 'corporate-law';

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const clientBenefits = {
    'corporate-law': {
      en: {
        title: 'Why Choose Our Corporate Law Services',
        benefits: [
          {
            icon: 'Award',
            title: 'Proven Track Record',
            description: 'Over 15 years of successful corporate law practice with hundreds of satisfied clients across various industries.'
          },
          {
            icon: 'Users',
            title: 'Expert Legal Team',
            description: 'Experienced attorneys specializing in corporate law with deep understanding of Kenyan business regulations.'
          },
          {
            icon: 'Clock',
            title: 'Timely Service Delivery',
            description: 'Efficient handling of corporate matters with strict adherence to deadlines and regulatory timelines.'
          },
          {
            icon: 'Shield',
            title: 'Risk Mitigation',
            description: 'Proactive identification and management of legal risks to protect your business interests and investments.'
          },
          {
            icon: 'TrendingUp',
            title: 'Strategic Business Advice',
            description: 'Legal counsel that aligns with your business objectives and supports sustainable growth strategies.'
          },
          {
            icon: 'Handshake',
            title: 'Personalized Attention',
            description: 'Dedicated service with direct access to senior partners and customized legal solutions for your business.'
          }
        ]
      },
      sw: {
        title: 'Kwa Nini Uchague Huduma Zetu za Sheria ya Makampuni',
        benefits: [
          {
            icon: 'Award',
            title: 'Rekodi ya Mafanikio',
            description: 'Zaidi ya miaka 15 ya utendaji mzuri wa sheria ya makampuni na mamia ya wateja walioridhi katika viwanda mbalimbali.'
          },
          {
            icon: 'Users',
            title: 'Timu ya Kitaalamu ya Kisheria',
            description: 'Mawakili wenye uzoefu wanaofanya kazi katika sheria ya makampuni na uelewa wa kina wa kanuni za biashara za Kenya.'
          },
          {
            icon: 'Clock',
            title: 'Utoaji wa Huduma kwa Wakati',
            description: 'Kushughulikia kwa ufanisi mambo ya makampuni kwa kufuata kwa ukali tarehe za mwisho na ratiba za kanuni.'
          },
          {
            icon: 'Shield',
            title: 'Kupunguza Hatari',
            description: 'Kutambua na kusimamia kwa kutangulia hatari za kisheria kulinda maslahi ya biashara yako na uwekezaji.'
          },
          {
            icon: 'TrendingUp',
            title: 'Ushauri wa Kimkakati wa Biashara',
            description: 'Ushauri wa kisheria unaooana na malengo ya biashara yako na kuunga mkono mikakati ya ukuaji endelevu.'
          },
          {
            icon: 'Handshake',
            title: 'Umakini wa Kibinafsi',
            description: 'Huduma ya kujitolea na upatikanaji wa moja kwa moja kwa washirika wakuu na suluhisho za kisheria zilizobinafsishwa kwa biashara yako.'
          }
        ]
      }
    },
    'family-law': {
      en: {
        title: 'Why Choose Our Family Law Services',
        benefits: [
          {
            icon: 'Heart',
            title: 'Compassionate Approach',
            description: 'Understanding and empathetic handling of sensitive family matters with focus on emotional well-being.'
          },
          {
            icon: 'Shield',
            title: 'Protecting Your Rights',
            description: 'Aggressive advocacy to ensure your legal rights and interests are fully protected throughout the process.'
          },
          {
            icon: 'Users',
            title: 'Child-Focused Solutions',
            description: 'Prioritizing the best interests of children in all family law matters and custody arrangements.'
          },
          {
            icon: 'Scale',
            title: 'Fair Resolutions',
            description: 'Commitment to achieving equitable outcomes that serve the interests of all family members.'
          },
          {
            icon: 'Clock',
            title: 'Efficient Process',
            description: 'Streamlined procedures to minimize stress and resolve family matters as quickly as possible.'
          },
          {
            icon: 'MessageCircle',
            title: 'Clear Communication',
            description: 'Regular updates and transparent communication throughout your family law case proceedings.'
          }
        ]
      },
      sw: {
        title: 'Kwa Nini Uchague Huduma Zetu za Sheria ya Familia',
        benefits: [
          {
            icon: 'Heart',
            title: 'Mbinu ya Huruma',
            description: 'Uelewa na kushughulikia kwa huruma mambo nyeti ya familia kwa kulenga ustawi wa kihisia.'
          },
          {
            icon: 'Shield',
            title: 'Kulinda Haki Zako',
            description: 'Utetezi mkali kuhakikisha haki zako za kisheria na maslahi yanalindwa kikamilifu katika mchakato wote.'
          },
          {
            icon: 'Users',
            title: 'Suluhisho Zinazolenga Watoto',
            description: 'Kuweka kipaumbele maslahi bora ya watoto katika mambo yote ya sheria ya familia na mipango ya ulinzi.'
          },
          {
            icon: 'Scale',
            title: 'Maamuzi ya Haki',
            description: 'Kujitolea kufikia matokeo ya haki yanayotumikia maslahi ya wanafamilia wote.'
          },
          {
            icon: 'Clock',
            title: 'Mchakato wa Ufanisi',
            description: 'Taratibu zilizoratibishwa kupunguza msongo wa mawazo na kutatua mambo ya familia haraka iwezekanavyo.'
          },
          {
            icon: 'MessageCircle',
            title: 'Mawasiliano Wazi',
            description: 'Masasisho ya kawaida na mawasiliano ya uwazi katika michakato ya kesi yako ya sheria ya familia.'
          }
        ]
      }
    },
    'property-law': {
      en: {
        title: 'Why Choose Our Property Law Services',
        benefits: [
          {
            icon: 'Home',
            title: 'Property Expertise',
            description: 'Specialized knowledge in Kenyan property law with extensive experience in real estate transactions.'
          },
          {
            icon: 'Search',
            title: 'Thorough Due Diligence',
            description: 'Comprehensive property searches and investigations to ensure clear title and identify potential issues.'
          },
          {
            icon: 'Shield',
            title: 'Risk Protection',
            description: 'Proactive identification and mitigation of property-related risks to protect your investments.'
          },
          {
            icon: 'FileText',
            title: 'Complete Documentation',
            description: 'Meticulous preparation of all legal documents required for property transactions and transfers.'
          },
          {
            icon: 'Clock',
            title: 'Efficient Transactions',
            description: 'Streamlined processes to ensure timely completion of property deals and registrations.'
          },
          {
            icon: 'TrendingUp',
            title: 'Investment Guidance',
            description: 'Strategic advice on property investments and portfolio development for maximum returns.'
          }
        ]
      },
      sw: {
        title: 'Kwa Nini Uchague Huduma Zetu za Sheria ya Mali',
        benefits: [
          {
            icon: 'Home',
            title: 'Utaalamu wa Mali',
            description: 'Ujuzi maalum katika sheria ya mali ya Kenya na uzoefu mkubwa katika miamala ya mali.'
          },
          {
            icon: 'Search',
            title: 'Utafiti wa Kina',
            description: 'Utafutaji na uchunguzi wa kina wa mali kuhakikisha hati safi na kutambua masuala yanayoweza kutokea.'
          },
          {
            icon: 'Shield',
            title: 'Ulinzi wa Hatari',
            description: 'Kutambua na kupunguza kwa kutangulia hatari zinazohusiana na mali kulinda uwekezaji wako.'
          },
          {
            icon: 'FileText',
            title: 'Nyaraka Kamili',
            description: 'Maandalizi ya makini ya nyaraka zote za kisheria zinazohitajika kwa miamala na uhamisho wa mali.'
          },
          {
            icon: 'Clock',
            title: 'Miamala ya Ufanisi',
            description: 'Michakato iliyoratibishwa kuhakikisha ukamilishaji wa wakati wa mikataba ya mali na usajili.'
          },
          {
            icon: 'TrendingUp',
            title: 'Uongozi wa Uwekezaji',
            description: 'Ushauri wa kimkakati kuhusu uwekezaji wa mali na maendeleo ya mfuko kwa faida za juu zaidi.'
          }
        ]
      }
    },
    'criminal-defense': {
      en: {
        title: 'Why Choose Our Criminal Defense Services',
        benefits: [
          {
            icon: 'Shield',
            title: 'Aggressive Defense',
            description: 'Vigorous representation and strategic defense to protect your rights and achieve the best possible outcome.'
          },
          {
            icon: 'Scale',
            title: 'Court Experience',
            description: 'Extensive courtroom experience with proven success in criminal defense cases across all levels of courts.'
          },
          {
            icon: 'Clock',
            title: '24/7 Availability',
            description: 'Round-the-clock availability for urgent matters, emergency consultations, and immediate legal assistance.'
          },
          {
            icon: 'Search',
            title: 'Thorough Investigation',
            description: 'Comprehensive case investigation and evidence analysis to build the strongest possible defense strategy.'
          },
          {
            icon: 'Users',
            title: 'Client Support',
            description: 'Dedicated support and guidance throughout the criminal justice process with regular case updates.'
          },
          {
            icon: 'Award',
            title: 'Proven Results',
            description: 'Track record of successful outcomes including case dismissals, reduced charges, and favorable plea agreements.'
          }
        ]
      },
      sw: {
        title: 'Kwa Nini Uchague Huduma Zetu za Utetezi wa Jinai',
        benefits: [
          {
            icon: 'Shield',
            title: 'Utetezi Mkali',
            description: 'Uwakilishi mkali na utetezi wa kimkakati kulinda haki zako na kufikia matokeo bora zaidi yanayowezekana.'
          },
          {
            icon: 'Scale',
            title: 'Uzoefu wa Mahakama',
            description: 'Uzoefu mkubwa wa chumba cha mahakama na mafanikio yaliyothibitishwa katika kesi za utetezi wa jinai katika ngazi zote za mahakama.'
          },
          {
            icon: 'Clock',
            title: 'Upatikanaji wa Saa 24/7',
            description: 'Upatikanaji wa mchana na usiku kwa mambo ya haraka, mazungumzo ya dharura, na msaada wa haraka wa kisheria.'
          },
          {
            icon: 'Search',
            title: 'Uchunguzi wa Kina',
            description: 'Uchunguzi wa kina wa kesi na uchambuzi wa ushahidi kujenga mkakati imara zaidi wa utetezi.'
          },
          {
            icon: 'Users',
            title: 'Msaada wa Mteja',
            description: 'Msaada na uongozi wa kujitolea katika mchakato wa haki za jinai na masasisho ya kawaida ya kesi.'
          },
          {
            icon: 'Award',
            title: 'Matokeo Yaliyothibitishwa',
            description: 'Rekodi ya matokeo ya mafanikio ikiwa ni pamoja na kufutwa kwa kesi, kupunguzwa kwa mashtaka, na mikataba ya kuomba kwa faida.'
          }
        ]
      }
    }
  };

  const currentBenefits = clientBenefits[practiceArea] || clientBenefits['corporate-law'];
  const content = currentBenefits[currentLanguage];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-4">
            {content.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full"></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {content.benefits.map((benefit, index) => (
            <div
              key={index}
              className="group relative bg-gradient-to-br from-surface/5 to-primary/5 rounded-2xl p-8 border border-border-light hover:shadow-lg transition-all duration-300"
            >
              <div className="text-center space-y-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl group-hover:scale-110 transition-transform duration-300">
                  <Icon name={benefit.icon} size={28} color="white" strokeWidth={2} />
                </div>
                
                <h3 className="text-xl font-heading font-semibold text-text-primary">
                  {benefit.title}
                </h3>
                
                <p className="text-text-secondary leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 border border-primary/20">
            <h3 className="text-2xl font-heading font-bold text-text-primary mb-4">
              {currentLanguage === 'en' ?'Ready to Get Started?' :'Uko Tayari Kuanza?'
              }
            </h3>
            <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
              {currentLanguage === 'en' ?'Contact us today for a consultation and discover how our expertise can help you achieve your legal objectives.' :'Wasiliana nasi leo kwa mazungumzo na ugundua jinsi utaalamu wetu unavyoweza kukusaidia kufikia malengo yako ya kisheria.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  const message = encodeURIComponent(currentLanguage === 'en' ?'Hello, I would like to schedule a consultation for legal services.' :'Hujambo, ningependa kupanga mazungumzo kwa huduma za kisheria.');
                  window.open(`https://wa.me/254721320787?text=${message}`, '_blank');
                }}
                className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-accent transition-colors duration-200"
              >
                <Icon name="MessageCircle" size={20} className="mr-2" />
                {currentLanguage === 'en' ? 'WhatsApp Consultation' : 'Mazungumzo ya WhatsApp'}
              </button>
              
              <button
                onClick={() => window.open('tel:+254721320787', '_self')}
                className="inline-flex items-center justify-center px-6 py-3 border border-primary text-primary rounded-xl font-medium hover:bg-primary hover:text-white transition-all duration-200"
              >
                <Icon name="Phone" size={20} className="mr-2" />
                {currentLanguage === 'en' ? 'Call Now' : 'Piga Simu Sasa'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientBenefits;